import * as React from 'react';
import { useCallback, useState } from 'react';

/** Material UI Packages. */
import { Box, Chip, Link, Typography } from '@mui/material';

/** Internal Components. */
import AppliedFilterChips from 'shared/ui/AppliedFilterChips';
import FilterButtonPopover from 'shared/ui/FilterButtonPopover';
import { SearchableOptionList } from 'shared/ui/FilterOptionLists';
import { formatMultiValueText, optionText } from 'shared/lib/filterLabel';

import { multiSelectMode, rolesForShipmentType } from '../lib/dataExportTradePartners';

/** Rows a popover lists at once; typing narrows the rest. */
const MAX_SHOWN_OPTIONS = 100;

const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const inlineLinkSx = { fontSize: 11, verticalAlign: 'baseline', cursor: 'pointer' };

/** The partner list is loaded with the page, so typing searches it here. */
function useLocalOptions(options) {
    const [shown, setShown] = useState([]);
    const search = useCallback(
        (term) => {
            const needle = term.trim().toLowerCase();
            const matched = needle ? options.filter((o) => o.name.toLowerCase().includes(needle)) : options;
            setShown(matched.slice(0, MAX_SHOWN_OPTIONS));
        },
        [options]
    );
    return { shown, search };
}

/** One role's picker. `multiple` false shows the picks as one selectable row each. */
function RoleFilter({ role, options, value, onApply, multiple, invalid, notice = null, blocked = null }) {
    const { shown, search } = useLocalOptions(options);
    return (
        <FilterButtonPopover
            label={role.label}
            value={multiple ? value : (value[0] ?? null)}
            valueText={formatMultiValueText(value)}
            onApply={(v) => onApply(asList(v))}
            resetValue={multiple ? [] : null}
            invalid={invalid}
            notice={notice}
            blocked={blocked}
        >
            {(draft, setDraft) => (
                <SearchableOptionList
                    options={shown}
                    value={draft}
                    onChange={setDraft}
                    onSearch={search}
                    placeholder={`Search ${role.label.toLowerCase()}`}
                    multiple={multiple}
                />
            )}
        </FilterButtonPopover>
    );
}

/** Says why a role can take one partner only, with a link that lifts it. */
function SingleModeNotice({ reason, clearLabel, onClear, several }) {
    return (
        <span>
            Single {several} only: {reason}{' '}
            <Link component="button" type="button" underline="hover" sx={inlineLinkSx} onClick={onClear}>
                Clear {clearLabel}
            </Link>{' '}
            to pick several.
        </span>
    );
}

/** Shown instead of the list while other picks make this role unusable; dropping a chip lifts it. */
function BlockedByPicks({ message, picks, onRemove }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#003a52' }}>{message}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {picks.map(({ key, label, option }) => (
                    <Chip
                        key={`${key}:${option.id}`}
                        size="small"
                        label={`${label}: ${optionText(option)}`}
                        onDelete={() => onRemove(key, option)}
                        sx={{ fontSize: '12px', height: 24 }}
                    />
                ))}
            </Box>
        </Box>
    );
}

/**
 * Trade partner conditions, one button per role. A shipment part is exported only when every
 * role picked is written on it or above it; several partners in one role mean any of them.
 *
 * @param {object} value - { client, shipper, consignee, forwarder, trucker }, each a list of { id, name }.
 * @param {object} options - Same keys, the partners each role can pick from.
 * @param {number} shipmentType - Roles the type does not have are not shown.
 * @param {function} onChange - Called with the whole new value.
 * @param {boolean} invalid - The page rejected CREATE because no trade partner is picked.
 */
export default function DataExportTradePartnerFilters({ value, options, shipmentType, onChange, invalid = false }) {
    const roles = rolesForShipmentType(shipmentType);
    const mode = multiSelectMode(value);
    const set = (key, list) => onChange({ ...value, [key]: list });
    const remove = (key, option) => set(key, value[key].filter((o) => String(o.id) !== String(option.id)));
    const picksOf = (keys) =>
        roles
            .filter((role) => keys.includes(role.key))
            .flatMap((role) => value[role.key].map((option) => ({ key: role.key, label: role.label, option })));

    const clientNotice = mode.clientMultiple ? (
        'Select one or several clients.'
    ) : (
        <SingleModeNotice
            several="client"
            reason="a shipper or consignee is set."
            clearLabel="shipper/consignee"
            onClear={() => onChange({ ...value, shipper: [], consignee: [] })}
        />
    );
    const houseNotice = mode.houseMultiple ? (
        'Select one or several.'
    ) : (
        <SingleModeNotice
            several="partner"
            reason="a client is set."
            clearLabel="client"
            onClear={() => set('client', [])}
        />
    );
    const clientBlocked = mode.clientBlocked ? (
        <BlockedByPicks
            message="Several shippers or consignees are selected. Keep one of each to use this filter:"
            picks={picksOf(['shipper', 'consignee'])}
            onRemove={remove}
        />
    ) : null;
    const houseBlocked = mode.houseBlocked ? (
        <BlockedByPicks
            message="Several clients are selected. Keep one of these to use this filter:"
            picks={picksOf(['client'])}
            onRemove={remove}
        />
    ) : null;

    const filterProps = (role) => {
        if (role.key === 'client') {
            return { multiple: mode.clientMultiple, notice: clientNotice, blocked: clientBlocked };
        }
        if (role.key === 'shipper' || role.key === 'consignee') {
            return { multiple: mode.houseMultiple, notice: houseNotice, blocked: houseBlocked };
        }
        return { multiple: true };
    };

    const chips = picksOf(roles.map((role) => role.key)).map(({ key, label, option }) => ({
        key: `${key}:${option.id}`,
        label,
        text: optionText(option),
    }));
    const removeChip = (chipKey) => {
        const [key, id] = chipKey.split(':');
        remove(key, { id });
    };
    const clearAll = () => onChange(Object.fromEntries(Object.keys(value).map((key) => [key, []])));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '38vw' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {roles.map((role) => (
                    <RoleFilter
                        key={role.key}
                        role={role}
                        options={options[role.key] ?? []}
                        value={value[role.key]}
                        onApply={(list) => set(role.key, list)}
                        invalid={invalid}
                        {...filterProps(role)}
                    />
                ))}
            </Box>
            <AppliedFilterChips filters={chips} onRemove={removeChip} onClearAll={clearAll} />
        </Box>
    );
}
